import React, { useState, useEffect } from 'react';
import { getAllUsers, db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx-js-style';
import { getCantieri } from '../../firebase';
import { applyTemplateToSummarySheet, createStyledCellFromTemplate } from './ExcelFormattingTemplate';

const AdminReports = () => {
  const [users, setUsers] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // ✨ STATI PER IL FILTRO CANTIERE
  const [selectedCantiere, setSelectedCantiere] = useState('all');
  const [searchCantiereTerm, setSearchCantiereTerm] = useState('');
  const [filteredCantieri, setFilteredCantieri] = useState([]);
  const [showCantiereSuggestions, setShowCantiereSuggestions] = useState(false);
  
  // ✨ NUOVI STATI PER IL FILTRO AZIENDA
  const [selectedAzienda, setSelectedAzienda] = useState('all');
  const [searchAziendaTerm, setSearchAziendaTerm] = useState('');
  const [aziende, setAziende] = useState([]);
  const [filteredAziende, setFilteredAziende] = useState([]);
  const [showAziendaSuggestions, setShowAziendaSuggestions] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Lista festività italiane 2024-2026
  const holidays = [
    '2024-01-01', '2024-01-06', '2024-04-01', '2024-04-25', '2024-05-01',
    '2024-06-02', '2024-08-15', '2024-11-01', '2024-12-08', '2024-12-25', '2024-12-26',
    '2025-01-01', '2025-01-06', '2025-04-20', '2025-04-21', '2025-04-25', '2025-05-01',
    '2025-06-02', '2025-08-15', '2025-11-01', '2025-12-08', '2025-12-25', '2025-12-26',
    '2026-01-01', '2026-01-06', '2026-04-05', '2026-04-06', '2026-04-25', '2026-05-01',
    '2026-06-02', '2026-08-15', '2026-11-01', '2026-12-08', '2026-12-25', '2026-12-26'
  ];
  
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2].sort((a, b) => b - a);
  };
  
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const isHoliday = (dateStr) => {
    return holidays.includes(dateStr);
  };

  const isWeekend = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const allUsers = await getAllUsers();
        
        // ✨ NUOVO: Filtra gli account disabilitati
        const activeUsers = allUsers.filter(user => {
          // Escludi gli account disabilitati
          const isActive = user.accountStatus?.active !== false;
          if (!isActive) {
            console.log(`⛔ Escluso account disabilitato: ${user.nome || ''} ${user.cognome || ''} (${user.email})`);
          }
          return isActive;
        });
        
        // Ordina alfabeticamente per cognome, poi per nome se i cognomi sono uguali
        const sortedUsers = activeUsers.sort((a, b) => {
          const aCognome = (a.cognome || a.email).toLowerCase();
          const bCognome = (b.cognome || b.email).toLowerCase();
          const aNome = (a.nome || '').toLowerCase();
          const bNome = (b.nome || '').toLowerCase();
          
          // Prima confronta i cognomi
          const cognomeCompare = aCognome.localeCompare(bCognome, 'it', { sensitivity: 'base' });
          if (cognomeCompare !== 0) {
            return cognomeCompare;
          }
          
          // Se i cognomi sono uguali, confronta i nomi
          return aNome.localeCompare(bNome, 'it', { sensitivity: 'base' });
        });
        
        setUsers(sortedUsers);

        // ✨ ESTRAZIONE AZIENDE UNICHE DAI DIPENDENTI
        const aziendeUniche = [...new Set(allUsers
          .map(user => user.azienda)
          .filter(azienda => azienda && azienda.trim() !== '')
        )].sort();
        setAziende(aziendeUniche);
        console.log(`${aziendeUniche.length} aziende trovate:`, aziendeUniche);

        const cantieriRef = collection(db, "cantieri");
        const cantieriSnapshot = await getDocs(cantieriRef);
        const cantieriData = cantieriSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCantieri(cantieriData);
        
      } catch (error) {
        console.error("Errore nel caricamento dati:", error);
        showNotification("Errore nel caricamento dati", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // ✨ Carica cantieri attivi
  useEffect(() => {
    const fetchCantieri = async () => {
      try {
        const cantieriData = await getCantieri({});
        const cantieriAttivi = cantieriData.filter(c => c.stato === 'Attivo');
        setCantieri(cantieriAttivi);
        console.log(`${cantieriAttivi.length} cantieri attivi caricati`);
      } catch (error) {
        console.error("Errore nel caricamento dei cantieri:", error);
        showNotification("Errore nel caricamento dei cantieri", "error");
      }
    };
    
    fetchCantieri();
  }, []);

  // ✨ Filtra cantieri in base alla ricerca
  useEffect(() => {
    if (searchCantiereTerm.trim() === '') {
      setFilteredCantieri([]);
      setShowCantiereSuggestions(false);
      return;
    }

    const filtered = cantieri.filter(cantiere => {
      const searchLower = searchCantiereTerm.toLowerCase();
      return (
        (cantiere.nomeCommessa?.toLowerCase().includes(searchLower)) ||
        (cantiere.contratto?.toLowerCase().includes(searchLower)) ||
        (cantiere.luogo?.toLowerCase().includes(searchLower))
      );
    });

    setFilteredCantieri(filtered);
    setShowCantiereSuggestions(filtered.length > 0);
  }, [searchCantiereTerm, cantieri]);

  // ✨ NUOVO: Filtra aziende in base alla ricerca
  useEffect(() => {
    if (searchAziendaTerm.trim() === '') {
      setFilteredAziende([]);
      setShowAziendaSuggestions(false);
      return;
    }

    const filtered = aziende.filter(azienda => 
      azienda.toLowerCase().includes(searchAziendaTerm.toLowerCase())
    );

    setFilteredAziende(filtered);
    setShowAziendaSuggestions(filtered.length > 0);
  }, [searchAziendaTerm, aziende]);

  // Filtra utenti in base alla ricerca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowSuggestions(false);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      const fullName = user.nome && user.cognome 
        ? `${user.nome} ${user.cognome}`.toLowerCase()
        : user.email.toLowerCase();
      
      return fullName.includes(searchLower) || 
             (user.email && user.email.toLowerCase().includes(searchLower));
    });

    setFilteredUsers(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [searchTerm, users]);

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const getUserWorkHoursByMonth = async (userId, month, year) => {
    try {
      const normalizedMonth = month.toString().replace(/^0+/, '') || month;
      const docId = `${userId}_${normalizedMonth}_${year}`;
      
      const docRef = doc(db, "workHours", docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.entries && Array.isArray(data.entries)) {
          data.entries = data.entries.map(entry => ({
            ...entry,
            overtime: entry.overtime !== undefined ? entry.overtime : 0
          }));
        }
        return { id: docSnap.id, ...data };
      }
      
      if (normalizedMonth !== month) {
        const altDocId = `${userId}_${month}_${year}`;
        const altDocRef = doc(db, "workHours", altDocId);
        const altDocSnap = await getDoc(altDocRef);
        
        if (altDocSnap.exists()) {
          const altData = altDocSnap.data();
          if (altData.entries && Array.isArray(altData.entries)) {
            altData.entries = altData.entries.map(entry => ({
              ...entry,
              overtime: entry.overtime !== undefined ? entry.overtime : 0
            }));
          }
          return { id: altDocSnap.id, ...altData };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Errore nel recupero delle ore lavorative:", error);
      return null;
    }
  };

  // ✨ OTTIMIZZAZIONE: Recupera i workHours di tutti gli utenti in batch
  const getAllUsersWorkHoursByMonth = async (userIds, month, year) => {
    try {
      const normalizedMonth = month.toString().replace(/^0+/, '') || month;
      const workHoursCache = new Map();
      
      // Crea array di promesse per recupero parallelo
      const promises = userIds.map(async (userId) => {
        const docId = `${userId}_${normalizedMonth}_${year}`;
        const docRef = doc(db, "workHours", docId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.entries && Array.isArray(data.entries)) {
            data.entries = data.entries.map(entry => ({
              ...entry,
              overtime: entry.overtime !== undefined ? entry.overtime : 0
            }));
          }
          return { userId, data: { id: docSnap.id, ...data } };
        }
        
        // Prova con formato alternativo
        if (normalizedMonth !== month) {
          const altDocId = `${userId}_${month}_${year}`;
          const altDocRef = doc(db, "workHours", altDocId);
          const altDocSnap = await getDoc(altDocRef);
          
          if (altDocSnap.exists()) {
            const altData = altDocSnap.data();
            if (altData.entries && Array.isArray(altData.entries)) {
              altData.entries = altData.entries.map(entry => ({
                ...entry,
                overtime: entry.overtime !== undefined ? entry.overtime : 0
              }));
            }
            return { userId, data: { id: altDocSnap.id, ...altData } };
          }
        }
        
        return { userId, data: null };
      });
      
      const results = await Promise.all(promises);
      results.forEach(({ userId, data }) => {
        workHoursCache.set(userId, data);
      });
      
      return workHoursCache;
    } catch (error) {
      console.error("Errore nel recupero batch delle ore lavorative:", error);
      return new Map();
    }
  };

  const getRapportiniByDate = async (date) => {
    try {
      const rapportiniRef = collection(db, "rapportini");
      const q = query(
        rapportiniRef,
        where("data", "==", new Date(date)),
        where("stato", "==", "Attivo")
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Errore nel recupero rapportini:", error);
      return [];
    }
  };

  // ✨ OTTIMIZZAZIONE: Recupera tutti i rapportini del mese in una volta sola
const getAllRapportiniByMonth = async (month, year) => {
  try {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59);

    const rapportiniRef = collection(db, "rapportini");
    const q = query(
      rapportiniRef,
      where("data", ">=", startDate),
      where("data", "<=", endDate)
    );

    const snapshot = await getDocs(q);
    const rapportiniByDate = new Map();

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const dateKey = data.data?.toDate?.().toISOString().split('T')[0];

      if (dateKey) {
        if (!rapportiniByDate.has(dateKey)) {
          rapportiniByDate.set(dateKey, []);
        }
        rapportiniByDate.get(dateKey).push({
          id: doc.id,
          ...data
        });
      }
    });

    return rapportiniByDate;
  } catch (error) {
    console.error("Errore nel recupero rapportini del mese:", error);
    return new Map();
  }
};


  const getCantiereInfoFromRapportini = async (userId, date) => {
    try {
      const rapportini = await getRapportiniByDate(date);
      
      for (const rapportino of rapportini) {
        const personaPresente = rapportino.personalePresente?.find(p => p.personaId === userId);
        if (personaPresente) {
          const cantiere = cantieri.find(c => c.id === rapportino.cantiereId);
          return {
            cantiere: cantiere ? cantiere.nomeCommessa : `Cantiere ID: ${rapportino.cantiereId}`,
            cantiereId: rapportino.cantiereId,
            oreLavorate: personaPresente.oreLavorate || 0,
            oreStandard: personaPresente.oreStandard || 0,
            oreStraordinario: personaPresente.oreStraordinario || 0,
            note: personaPresente.notePersonale || ''
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Errore nel recupero info cantiere:", error);
      return null;
    }
  };

  const generateAllDaysInMonth = (month, year) => {
    const days = [];
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(yearNum, monthNum - 1, day);
      const dayName = date.toLocaleDateString('it-IT', { weekday: 'long' });
      const dateStr = `${yearNum}-${monthNum.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      days.push({
        date: dateStr,
        day: dayName,
        dayNumber: day
      });
    }
    
    return days;
  };

  // 🌙 Carica tutti i turni timekeeping di un utente per un mese (per rilevare turni notturni)
const getTimekeepingByUserMonth = async (userId, month, year) => {
  try {
    const startDate = `${year}-${String(parseInt(month)).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(parseInt(month)).padStart(2, '0')}-${new Date(year, parseInt(month), 0).getDate().toString().padStart(2, '0')}`;

    const q = query(
      collection(db, 'timekeeping'),
      where('userId', '==', userId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('status', '==', 'completed')
    );

    const snapshot = await getDocs(q);
    const records = [];
    snapshot.forEach(doc => records.push({ id: doc.id, ...doc.data() }));
    console.log(`🌙 Timekeeping ${userId} ${month}/${year}: ${records.length} turni completati`);
    return records;
  } catch (error) {
    console.error('Errore caricamento timekeeping:', error);
    return [];
  }
};

const generateUserReportWithCantieri = async (userId, userName, cognome = '', nome = '', rapportiniCache = null, workHoursCache = null) => {
  try {
    const rawMonth = selectedMonth.replace(/^0+/, '');
    const allDays = generateAllDaysInMonth(parseInt(rawMonth), parseInt(selectedYear));

    // ✨ OTTIMIZZAZIONE: Usa cache workHours se disponibile
    const workHoursData = workHoursCache && workHoursCache.has(userId)
      ? workHoursCache.get(userId)
      : await getUserWorkHoursByMonth(userId, rawMonth, selectedYear);

    // 🌙 Carica timekeeping per rilevare turni notturni (inizio >= 16:00)
    const timekeepingRecords = await getTimekeepingByUserMonth(userId, rawMonth, selectedYear);

    const reportData = [];
    const cantieriTotali = new Map();

    let totalStandardHours = 0;
    let totalOvertimeHours = 0;
    let specialDaysCounts = { M: 0, P: 0, A: 0, F: 0, C: 0, CIG: 0 };

    for (const dayInfo of allDays) {
      let dayData = {
        date: dayInfo.date,
        day: dayInfo.day,
        standard: 0,
        overtime: 0,
        cantiere: '',
        note: '',
        nightHours: 0,
        isWeekend: isWeekend(dayInfo.date),
        isHoliday: isHoliday(dayInfo.date)
      };

      if (workHoursData && workHoursData.entries) {
        const entry = workHoursData.entries.find(e => e.date === dayInfo.date);

        if (entry) {
          const normalizedTotal = String(entry.total).trim().toUpperCase();

          if (["M", "P", "A", "F", "C", "CIG"].includes(normalizedTotal)) {
            dayData.standard = normalizedTotal;
            dayData.overtime = '';
            dayData.cantiere = '';
            dayData.note = getSpecialDayDescription(normalizedTotal);

            const countKey = normalizedTotal === "C" ? "C" : normalizedTotal;
            specialDaysCounts[countKey]++;
          } else {
            const standardHours = parseInt(entry.total) || 0;
            const overtimeHours = parseInt(entry.overtime) || 0;

            dayData.standard = standardHours;
            dayData.overtime = overtimeHours;
            dayData.note = entry.notes || '';

            totalStandardHours += standardHours;
            totalOvertimeHours += overtimeHours;

            // ✨ OTTIMIZZAZIONE: Usa cache rapportini invece di query ripetute
            let cantiereInfo = null;
            if (rapportiniCache && rapportiniCache.has(dayInfo.date)) {
              const rapportini = rapportiniCache.get(dayInfo.date);
              for (const rapportino of rapportini) {
                const personaPresente = rapportino.personalePresente?.find(p => p.personaId === userId);
                if (personaPresente) {
                  const cantiere = cantieri.find(c => c.id === rapportino.cantiereId);
                  cantiereInfo = {
                    cantiere: cantiere
                      ? cantiere.nomeCommessa
                      : `Cantiere ID: ${rapportino.cantiereId}`,
                    cantiereId: rapportino.cantiereId
                  };
                  break;
                }
              }
            } else {
              // Fallback alla vecchia funzione se non c'è cache
              cantiereInfo = await getCantiereInfoFromRapportini(userId, dayInfo.date);
            }

            if (cantiereInfo) {
              dayData.cantiere = cantiereInfo.cantiere;

              const cantiereKey = cantiereInfo.cantiere;
              if (!cantieriTotali.has(cantiereKey)) {
                cantieriTotali.set(cantiereKey, { standard: 0, overtime: 0, total: 0 });
              }
              const cantiereData = cantieriTotali.get(cantiereKey);
              cantiereData.standard += standardHours;
              cantiereData.overtime += overtimeHours;
              cantiereData.total += standardHours + overtimeHours;
            } else {
              dayData.cantiere = 'Non specificato';
            }
          }
        }
      }

      // 🌙 Calcola ore notturne: turni completati con clockInTime >= 16:00 in questa data
      const nightShifts = timekeepingRecords.filter(rec => {
        if (rec.date !== dayInfo.date) return false;
        if (!rec.clockInTime) return false;
        const [h] = rec.clockInTime.split(':').map(Number);
        return h >= 16;
      });

      if (nightShifts.length > 0) {
        const totalNightHours = nightShifts.reduce((sum, rec) => {
          return sum + (parseFloat(rec.standardHours) || 0) + (parseFloat(rec.overtimeHours) || 0);
        }, 0);
        dayData.nightHours = Math.round(totalNightHours * 10) / 10;
      }

      reportData.push(dayData);
    }

    return {
      userName,
      cognome,
      nome,
      reportData,
      totals: {
        standardHours: totalStandardHours,
        overtimeHours: totalOvertimeHours,
        totalHours: totalStandardHours + totalOvertimeHours
      },
      specialDays: specialDaysCounts,
      cantieriBreakdown: Array.from(cantieriTotali.entries()).map(([cantiere, data]) => ({
        cantiere,
        ...data
      }))
    };
  } catch (error) {
    console.error(`Errore nella generazione del report per l'utente ${userId}:`, error);
    return null;
  }
};

  const getSpecialDayDescription = (code) => {
    const descriptions = {
      'M': 'Malattia',
      'P': 'Permesso',
      'A': 'Assenza',
      'F': 'Ferie',
      'C': 'Cassa Integrazione',
      'CIG': 'Cassa Integrazione'
    };
    return descriptions[code] || code;
  };

  const createStyledCell = (value, style) => {
    const cell = { v: value, t: typeof value === 'number' ? 'n' : 's' };
    
    if (style) {
      cell.s = {
        font: style.font || {},
        fill: style.fill || {},
        alignment: style.alignment || { horizontal: 'center', vertical: 'center' },
        border: style.border || {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
    }
    
    return cell;
  };

  const getUsersForExport = async () => {
    let usersToExport = [];
    
    if (selectedUser !== 'all') {
      const user = users.find(u => u.id === selectedUser);
      if (user) usersToExport = [user];
      
    } else if (selectedCantiere !== 'all') {
      try {
        console.log('🔍 DEBUG - Cantiere selezionato:', selectedCantiere);
        console.log('📅 DEBUG - Mese:', selectedMonth, 'Anno:', selectedYear);
        
        const rapportiniRef = collection(db, 'rapportini');
        
        const q = query(
          rapportiniRef,
          where('cantiereId', '==', selectedCantiere)
        );
        
        const querySnapshot = await getDocs(q);
        console.log('📊 DEBUG - Rapportini totali del cantiere:', querySnapshot.size);
        
        const userIds = new Set();
        let rapportiniCount = 0;
        
        querySnapshot.forEach((doc) => {
          const rapportino = doc.data();
          
          if (rapportino.nomeRapportino) {
            const rapportinoDate = new Date(rapportino.nomeRapportino);
            const rapportinoMonth = rapportinoDate.getMonth() + 1;
            const rapportinoYear = rapportinoDate.getFullYear();
            
            if (rapportinoMonth === parseInt(selectedMonth) && 
                rapportinoYear === parseInt(selectedYear)) {
              
              rapportiniCount++;
              console.log('📋 DEBUG - Rapportino valido:', rapportino.nomeRapportino);
              
              if (rapportino.personalePresente && Array.isArray(rapportino.personalePresente)) {
                console.log('✅ personalePresente con', rapportino.personalePresente.length, 'persone');
                
                rapportino.personalePresente.forEach((persona, index) => {
                  console.log(`👤 DEBUG - Persona ${index + 1}:`, persona);
                  
                  const personaId = persona.personaId || persona.userId || persona.id;
                  
                  if (personaId) {
                    console.log('✅ Aggiunto userId:', personaId);
                    userIds.add(personaId);
                  } else {
                    console.warn('⚠️ Persona senza ID:', persona);
                  }
                });
              }
            }
          }
        });
        
        console.log(`📊 DEBUG - Rapportini del mese ${selectedMonth}/${selectedYear}:`, rapportiniCount);
        console.log('👥 DEBUG - Lista completa userIds:', Array.from(userIds));
        
        if (rapportiniCount === 0) {
          showNotification("Nessun rapportino trovato per questo cantiere nel mese selezionato", "warning");
          return [];
        }
        
        if (userIds.size === 0) {
          showNotification("Nessun dipendente trovato nei rapportini del cantiere", "warning");
          return [];
        }
        
        usersToExport = users.filter(u => {
          const found = userIds.has(u.id);
          if (found) {
            console.log('✅ Match trovato per:', u.nome, u.cognome, '(id:', u.id + ')');
          }
          return found;
        });
        
        console.log('✅ DEBUG - Utenti finali da esportare:', usersToExport.length);
        
        if (usersToExport.length === 0) {
          console.error('❌ Nessun match tra userIds e users');
          console.log('UserIds cercati:', Array.from(userIds));
          console.log('IDs utenti disponibili (primi 5):', users.slice(0, 5).map(u => ({id: u.id, nome: u.nome})));
          showNotification("Nessun dipendente trovato per questo cantiere nel periodo selezionato", "warning");
          return [];
        }
        
      } catch (error) {
        console.error("❌ Errore nel recupero dipendenti cantiere:", error);
        showNotification(`Errore: ${error.message}`, "error");
        return [];
      }
      
    } else {
      console.log('📊 Export di tutti i dipendenti');
      usersToExport = users;
    }
    
 // ✨ NUOVO: Filtra account disabilitati PRIMA del filtro azienda
 usersToExport = usersToExport.filter(u => {
  // Escludi gli account disabilitati
  const isActive = u.accountStatus?.active !== false;
  if (!isActive) {
    console.log(`⛔ Escluso account disabilitato: ${u.nome} ${u.cognome}`);
  }
  return isActive;
});

    // ✨ NUOVO: Applica filtro azienda se selezionato
    if (selectedAzienda !== 'all') {
      usersToExport = usersToExport.filter(u => u.azienda === selectedAzienda);
      console.log(`✅ Filtrato per azienda "${selectedAzienda}": ${usersToExport.length} utenti`);
    }
    
    return usersToExport;
  };

  const generateExcelReport = async () => {
    try {
      setIsGenerating(true);
      
      if (selectedMonth === '') {
        showNotification("Seleziona un mese", "error");
        setIsGenerating(false);
        return;
      }
      
      const allReports = [];
      const usersToProcess = await getUsersForExport();

      if (usersToProcess.length === 0) {
        showNotification("Nessun utente da processare", "error");
        setIsGenerating(false);
        return;
      }
      
      console.log(`🚀 Inizio elaborazione di ${usersToProcess.length} utenti...`);
      const startTime = performance.now();
      
      // ✨ OTTIMIZZAZIONE: Recupera tutti i rapportini del mese una volta sola
      console.log('📦 Caricamento cache rapportini...');
      const rapportiniCache = await getAllRapportiniByMonth(parseInt(selectedMonth), parseInt(selectedYear));
      console.log(`✅ Cache rapportini caricata: ${rapportiniCache.size} giorni con rapportini`);
      
      // ✨ OTTIMIZZAZIONE: Recupera i workHours di tutti gli utenti in batch
      console.log('📦 Caricamento cache workHours...');
      const userIds = usersToProcess.map(u => u.id);
      const workHoursCache = await getAllUsersWorkHoursByMonth(userIds, selectedMonth, selectedYear);
      console.log(`✅ Cache workHours caricata: ${workHoursCache.size} utenti`);
      
      // ✨ OTTIMIZZAZIONE: Recupero parallelo dei dati invece di sequenziale
      const userReportPromises = usersToProcess.map(async (user) => {
        const userName = `${user.cognome || ''} ${user.nome || ''}`.trim() || user.email;
        
        try {
          const userReport = await generateUserReportWithCantieri(
            user.id, 
            userName, 
            user.cognome || '', 
            user.nome || '',
            rapportiniCache,  // ✨ Passa la cache rapportini
            workHoursCache    // ✨ Passa la cache workHours
          );
          
          if (userReport && userReport.reportData.length > 0) {
            const hasWorkData = userReport.reportData.some(day => 
              day.standard > 0 || day.overtime > 0 || ["M", "P", "A", "F", "C", "CIG"].includes(String(day.standard).toUpperCase())
            );
            
            // ✨ Se è un report per azienda, includi tutti i dipendenti anche senza ore
            if (hasWorkData || selectedAzienda !== 'all') {
              return userReport;
            }
          }
          return null;
        } catch (userError) {
          console.error(`Errore per utente ${userName}:`, userError);
          return null;
        }
      });
      
      // Attendi tutti i report in parallelo
      const userReports = await Promise.all(userReportPromises);
      allReports.push(...userReports.filter(report => report !== null));
      
      const endTime = performance.now();
      console.log(`✅ Elaborazione completata in ${((endTime - startTime) / 1000).toFixed(2)} secondi`);
      
      if (allReports.length === 0) {
        showNotification(`Nessun dato trovato per il periodo selezionato`, "warning");
        setIsGenerating(false);
        return;
      }
      
      const workbook = XLSX.utils.book_new();
      
      // ✨ DETERMINA IL NOME DELL'AZIENDA PER IL FOGLIO DI RIEPILOGO
      let companyName = "FD SRL"; // Default
      
      if (selectedAzienda !== 'all') {
        companyName = selectedAzienda;
      } else if (selectedUser !== 'all') {
        const user = users.find(u => u.id === selectedUser);
        if (user && user.azienda) {
          companyName = user.azienda;
        }
      } else {
        companyName = "Tutte le Aziende";
      }
      
      // FOGLIO RIEPILOGATIVO CON FORMATTAZIONE TEMPLATE
      // ✨ MODIFICATO: companyName ora è dinamico basato sui filtri
      const summarySheet = applyTemplateToSummarySheet(
        XLSX,
        workbook,
        "PRESENZE",
        companyName,
        parseInt(selectedMonth),
        selectedYear,
        allReports
      );

      if (summarySheet) {
        XLSX.utils.book_append_sheet(workbook, summarySheet, "PRESENZE");
      } else {
        console.warn("Fallback al foglio riepilogo classico");
        
        const fallbackSheet = {};
        const summaryRange = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
        
        fallbackSheet['A1'] = createStyledCell(
          `REPORT PRESENZE - ${getMonthName(selectedMonth).toUpperCase()} ${selectedYear}`,
          {
            font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1A237E' } },
            alignment: { horizontal: 'center', vertical: 'center' }
          }
        );
        
        XLSX.utils.book_append_sheet(workbook, fallbackSheet, "Riepilogo");
      }
      
      // FOGLI DETTAGLIO PER OGNI DIPENDENTE
      allReports.forEach(report => {
        try {
          const detailSheet = {};
          let row = 0;
          
          // Titolo
          detailSheet['A1'] = createStyledCell(
            report.userName.toUpperCase(),
            {
              font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '1A237E' } },
              alignment: { horizontal: 'center', vertical: 'center' }
            }
          );
          
          row++;
          detailSheet['A2'] = createStyledCell(
            `${getMonthName(selectedMonth)} ${selectedYear}`,
            {
              font: { bold: true, sz: 13, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '283593' } },
              alignment: { horizontal: 'center', vertical: 'center' }
            }
          );
          
          row += 2;
          
          // Headers tabella
          const detailHeaders = ['DATA', 'GIORNO', 'ORE STD', 'ORE STRAORD.', 'CANTIERE', 'NOTE'];
          detailHeaders.forEach((header, col) => {
            const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
            detailSheet[cellRef] = createStyledCell(header, {
              font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '3949AB' } },
              alignment: { horizontal: 'center', vertical: 'center' }
            });
          });
          
          row++;
          
          // Dati giorni
          report.reportData.forEach(day => {
            const normalizedStandard = String(day.standard).trim().toUpperCase();
            const isSpecialDay = ["M", "P", "A", "F", "C", "CIG"].includes(normalizedStandard);
            let cellStyle = {
              font: { sz: 10 },
              fill: { fgColor: { rgb: 'FFFFFF' } },
              alignment: { vertical: 'center' }
            };
            
            // Determina il colore di sfondo
            if (day.isHoliday) {
              cellStyle.fill = { fgColor: { rgb: 'FFCDD2' } }; // Rosso chiaro
              cellStyle.font = { sz: 10, color: { rgb: 'C62828' }, bold: true };
            } else if (isSpecialDay) {
              cellStyle.fill = { fgColor: { rgb: 'FFE0B2' } }; // Arancione chiaro
              cellStyle.font = { sz: 10, color: { rgb: 'F57C00' }, bold: true };
            } else if (day.isWeekend) {
              cellStyle.fill = { fgColor: { rgb: 'ECEFF1' } }; // Grigio chiaro
              cellStyle.font = { sz: 10, color: { rgb: '616161' } };
            }
            
            const rowData = [
              formatDate(day.date),
              day.day.charAt(0).toUpperCase() + day.day.slice(1),
              day.standard,
              day.overtime,
              day.cantiere,
              day.note
            ];
            
            rowData.forEach((value, col) => {
              const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
              const colStyle = { ...cellStyle };
              if (col === 0 || col === 1) {
                colStyle.alignment = { horizontal: 'left', vertical: 'center' };
              } else if (col === 4 || col === 5) {
                colStyle.alignment = { horizontal: 'left', vertical: 'center' };
              } else {
                colStyle.alignment = { horizontal: 'center', vertical: 'center' };
              }
              detailSheet[cellRef] = createStyledCell(value, colStyle);
            });
            
            row++;
          });
          
          // Righe totali
          row++;
          const totalsData = [
            ['TOTALE ORE STANDARD', '', report.totals.standardHours, '', '', ''],
            ['TOTALE ORE STRAORDINARIO', '', '', report.totals.overtimeHours, '', ''],
            ['TOTALE COMPLESSIVO', '', report.totals.totalHours, '', '', '']
          ];
          
          totalsData.forEach(totalRow => {
            totalRow.forEach((value, col) => {
              const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
              detailSheet[cellRef] = createStyledCell(value, {
                font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '2E7D32' } },
                alignment: { horizontal: 'center', vertical: 'center' }
              });
            });
            row++;
          });
          
          // Riepilogo cantieri
          if (report.cantieriBreakdown.length > 0) {
            row++;
            detailSheet[XLSX.utils.encode_cell({ r: row, c: 0 })] = createStyledCell(
              'RIEPILOGO CANTIERI',
              {
                font: { bold: true, sz: 13, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: 'D84315' } },
                alignment: { horizontal: 'center', vertical: 'center' }
              }
            );
            
            row++;
            const cantieriHeaders = ['CANTIERE', '', 'ORE STD', 'ORE STRAORD.', 'TOTALE', ''];
            cantieriHeaders.forEach((header, col) => {
              const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
              detailSheet[cellRef] = createStyledCell(header, {
                font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '3949AB' } },
                alignment: { horizontal: 'center', vertical: 'center' }
              });
            });
            
            row++;
            
            report.cantieriBreakdown.forEach(cantiere => {
              const rowData = [cantiere.cantiere, '', cantiere.standard, cantiere.overtime, cantiere.total + 'h', ''];
              
              rowData.forEach((value, col) => {
                const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                detailSheet[cellRef] = createStyledCell(value, {
                  font: { sz: 10 },
                  fill: { fgColor: { rgb: 'FFFFFF' } },
                  alignment: { horizontal: col === 0 ? 'left' : 'center', vertical: 'center' }
                });
              });
              
              row++;
            });
          }
          
          detailSheet['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row, c: 5 } });
          detailSheet['!cols'] = [
            { wch: 12 },
            { wch: 15 },
            { wch: 12 },
            { wch: 15 },
            { wch: 35 },
            { wch: 40 }
          ];
          
          // Merge titoli
          detailSheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }
          ];
          
          const sheetName = report.userName.length > 30 ? 
            report.userName.substring(0, 27) + "..." : 
            report.userName;
            
          XLSX.utils.book_append_sheet(workbook, detailSheet, sheetName);
        } catch (sheetError) {
          console.error(`Errore nella creazione foglio per ${report.userName}:`, sheetError);
        }
      });
      
      // Genera il file Excel con opzione cellStyles
      // ✨ MODIFICATO: Nome file dinamico basato sui filtri
      let fileName;
      if (selectedUser !== 'all') {
        fileName = `Report_${allReports[0]?.userName.replace(/\s+/g, '_')}_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`;
      } else if (selectedAzienda !== 'all') {
        fileName = `Report_${selectedAzienda.replace(/\s+/g, '_')}_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`;
      } else if (selectedCantiere !== 'all') {
        fileName = `Report_Cantiere_${searchCantiereTerm.replace(/\s+/g, '_')}_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`;
      } else {
        fileName = `Report_Presenze_Tutti_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`;
      }
      
      try {
        // Opzioni di scrittura con supporto stili
        const writeOptions = {
          bookType: 'xlsx',
          bookSST: false,
          type: 'binary',
          cellStyles: true
        };
        
        XLSX.writeFile(workbook, fileName, writeOptions);
        showNotification(`✅ Report Excel generato con successo per ${allReports.length} dipendente/i`, "success");
      } catch (writeError) {
        console.error('Errore nella scrittura del file:', writeError);
        showNotification(`❌ Errore nella generazione del file: ${writeError.message}`, "error");
      }
      
    } catch (error) {
      console.error("Errore nella generazione del report:", error);
      showNotification(`❌ Errore nella generazione del report: ${error.message}`, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const getMonthName = (monthValue) => {
    const months = [
      "Gennaio", "Febbraio", "Marzo", "Aprile", 
      "Maggio", "Giugno", "Luglio", "Agosto", 
      "Settembre", "Ottobre", "Novembre", "Dicembre"
    ];
    return months[parseInt(monthValue) - 1];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div style={{
      padding: '30px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '20px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        padding: '40px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          borderBottom: '3px solid #667eea',
          paddingBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '10px'
          }}>
            📊 Generazione Report Excel
          </h1>
          <p style={{
            color: '#7f8c8d',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Seleziona i filtri e genera report personalizzati delle presenze
          </p>
        </div>

        {notification.show && (
          <div style={{
            padding: '15px 20px',
            marginBottom: '25px',
            borderRadius: '12px',
            backgroundColor: notification.type === 'success' ? '#d4edda' : '#f8d7da',
            color: notification.type === 'success' ? '#155724' : '#721c24',
            border: `2px solid ${notification.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <span style={{ fontSize: '20px' }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            {notification.message}
          </div>
        )}

        {/* FILTRO UTENTE */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '10px',
            color: '#2c3e50',
            fontWeight: '700',
            fontSize: '16px',
            letterSpacing: '0.3px'
          }}>
            👤 Seleziona Dipendente
          </label>
          
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.trim() === '') {
                  setSelectedUser('all');
                }
              }}
              onFocus={() => {
                if (searchTerm.trim() !== '' && filteredUsers.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Cerca dipendente per nome o email..."
              style={{
                width: '100%',
                padding: '15px 45px 15px 20px',
                fontSize: '16px',
                border: '2px solid #e1e8ed',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#667eea'}
              onMouseLeave={(e) => e.target.style.borderColor = '#e1e8ed'}
            />
            
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedUser('all');
                  setShowSuggestions(false);
                }}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: '5px',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#e74c3c'}
                onMouseLeave={(e) => e.target.style.color = '#95a5a6'}
                title="Cancella ricerca"
              >
                ×
              </button>
            )}
            
            {showSuggestions && filteredUsers.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid #667eea',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
              }}>
                <div
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedUser('all');
                    setShowSuggestions(false);
                  }}
                  style={{
                    padding: '15px 20px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #ecf0f1',
                    fontWeight: '700',
                    color: '#667eea',
                    backgroundColor: '#f8f9fa',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f4fd'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                >
                  👥 Tutti i dipendenti
                </div>
                {filteredUsers.map((user) => {
                  const displayName = user.nome && user.cognome 
                    ? `${user.nome} ${user.cognome}`
                    : user.email;
                  
                  return (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSearchTerm(displayName);
                        setSelectedUser(user.id);
                        setShowSuggestions(false);
                      }}
                      style={{
                        padding: '15px 20px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #ecf0f1',
                        transition: 'background-color 0.2s',
                        backgroundColor: selectedUser === user.id ? '#e8f4fd' : 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = selectedUser === user.id ? '#e8f4fd' : 'white';
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                        {displayName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        {user.email}
                        {user.azienda && ` • ${user.azienda}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {selectedUser !== 'all' && (
            <div style={{
              marginTop: '12px',
              padding: '12px 18px',
              backgroundColor: '#e8f4fd',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <span style={{ color: '#1976d2', fontSize: '15px', fontWeight: '600' }}>
                ✓ Selezionato: <strong>{searchTerm}</strong>
              </span>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedUser('all');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  fontWeight: '600'
                }}
              >
                Rimuovi
              </button>
            </div>
          )}
        </div>

        {/* ✨ NUOVO: FILTRO AZIENDA */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '10px',
            color: '#2c3e50',
            fontWeight: '700',
            fontSize: '16px',
            letterSpacing: '0.3px'
          }}>
            🏢 Filtra per Azienda (opzionale)
          </label>
          
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchAziendaTerm}
              onChange={(e) => {
                setSearchAziendaTerm(e.target.value);
                if (e.target.value.trim() === '') {
                  setSelectedAzienda('all');
                }
              }}
              onFocus={() => {
                if (searchAziendaTerm.trim() !== '' && filteredAziende.length > 0) {
                  setShowAziendaSuggestions(true);
                } else if (aziende.length > 0) {
                  setFilteredAziende(aziende);
                  setShowAziendaSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowAziendaSuggestions(false), 200);
              }}
              placeholder="Cerca o seleziona un'azienda..."
              style={{
                width: '100%',
                padding: '15px 45px 15px 20px',
                fontSize: '16px',
                border: '2px solid #e1e8ed',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#667eea'}
              onMouseLeave={(e) => e.target.style.borderColor = '#e1e8ed'}
            />
            
            {searchAziendaTerm && (
              <button
                onClick={() => {
                  setSearchAziendaTerm('');
                  setSelectedAzienda('all');
                  setShowAziendaSuggestions(false);
                }}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: '5px',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#e74c3c'}
                onMouseLeave={(e) => e.target.style.color = '#95a5a6'}
                title="Cancella ricerca azienda"
              >
                ×
              </button>
            )}
            
            {showAziendaSuggestions && (filteredAziende.length > 0 || aziende.length > 0) && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid #667eea',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
              }}>
                <div
                  onClick={() => {
                    setSearchAziendaTerm('');
                    setSelectedAzienda('all');
                    setShowAziendaSuggestions(false);
                  }}
                  style={{
                    padding: '15px 20px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #ecf0f1',
                    fontWeight: '700',
                    color: '#667eea',
                    backgroundColor: '#f8f9fa',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f4fd'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                >
                  🏢 Tutte le Aziende
                </div>
                {(searchAziendaTerm ? filteredAziende : aziende).map((azienda) => (
                  <div
                    key={azienda}
                    onClick={() => {
                      setSearchAziendaTerm(azienda);
                      setSelectedAzienda(azienda);
                      setShowAziendaSuggestions(false);
                    }}
                    style={{
                      padding: '15px 20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #ecf0f1',
                      transition: 'background-color 0.2s',
                      backgroundColor: selectedAzienda === azienda ? '#e8f4fd' : 'white',
                      fontWeight: '600',
                      color: '#2c3e50'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = selectedAzienda === azienda ? '#e8f4fd' : 'white';
                    }}
                  >
                    {azienda}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {selectedAzienda !== 'all' && (
            <div style={{
              marginTop: '12px',
              padding: '12px 18px',
              backgroundColor: '#e8f4fd',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <span style={{ color: '#1976d2', fontSize: '15px', fontWeight: '600' }}>
                📌 Filtrato per: <strong>{searchAziendaTerm}</strong>
              </span>
              <button
                onClick={() => {
                  setSearchAziendaTerm('');
                  setSelectedAzienda('all');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  fontWeight: '600'
                }}
              >
                Rimuovi filtro
              </button>
            </div>
          )}
        </div>

        {/* FILTRO MESE E ANNO */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '25px'
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#2c3e50',
              fontWeight: '600',
              fontSize: '15px'
            }}>
              📅 Mese
            </label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            >
              <option value="">Seleziona mese</option>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i, 1).toLocaleDateString('it-IT', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#2c3e50',
              fontWeight: '600',
              fontSize: '15px'
            }}>
              📆 Anno
            </label>
            <select 
              value={selectedYear} 
              onChange={handleYearChange}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            >
              {generateYearOptions().map(year => (
                <option key={year} value={year.toString()}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* FILTRO CANTIERE */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: '#2c3e50',
            fontWeight: '600',
            fontSize: '15px'
          }}>
            🏗️ Filtra per Cantiere (opzionale)
          </label>
          
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchCantiereTerm}
              onChange={(e) => {
                setSearchCantiereTerm(e.target.value);
                if (e.target.value.trim() === '') {
                  setSelectedCantiere('all');
                }
              }}
              onFocus={() => {
                if (searchCantiereTerm.trim() !== '' && filteredCantieri.length > 0) {
                  setShowCantiereSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowCantiereSuggestions(false), 200);
              }}
              placeholder="Cerca cantiere per nome, contratto o luogo..."
              style={{
                width: '100%',
                padding: '12px 40px 12px 15px',
                fontSize: '15px',
                border: '2px solid #e1e8ed',
                borderRadius: '10px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: 'white'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#667eea'}
              onMouseLeave={(e) => e.target.style.borderColor = '#e1e8ed'}
            />
            
            {searchCantiereTerm && (
              <button
                onClick={() => {
                  setSearchCantiereTerm('');
                  setSelectedCantiere('all');
                  setShowCantiereSuggestions(false);
                }}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: '5px'
                }}
                title="Cancella ricerca cantiere"
              >
                ×
              </button>
            )}
            
            {showCantiereSuggestions && filteredCantieri.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid #667eea',
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {filteredCantieri.map((cantiere) => {
                  const displayName = cantiere.nomeCommessa || cantiere.contratto || 'Cantiere';
                  const subtitle = cantiere.luogo ? ` - ${cantiere.luogo}` : '';
                  
                  return (
                    <div
                      key={cantiere.id}
                      onClick={() => {
                        setSearchCantiereTerm(displayName);
                        setSelectedCantiere(cantiere.id);
                        setShowCantiereSuggestions(false);
                      }}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {displayName}
                      </div>
                      {subtitle && (
                        <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '2px' }}>
                          {subtitle}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {selectedCantiere !== 'all' && (
            <div style={{
              marginTop: '10px',
              padding: '10px 15px',
              backgroundColor: '#e8f4fd',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ color: '#1976d2', fontSize: '14px' }}>
                📌 Filtrato per: <strong>{searchCantiereTerm}</strong>
              </span>
              <button
                onClick={() => {
                  setSearchCantiereTerm('');
                  setSelectedCantiere('all');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textDecoration: 'underline'
                }}
              >
                Rimuovi filtro
              </button>
            </div>
          )}
        </div>
        
        <div className="action-buttons" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '15px',
          marginBottom: '30px'
        }}>
          <button 
            className="btn btn-primary" 
            onClick={generateExcelReport}
            disabled={isGenerating || selectedMonth === ''}
            style={{
              background: isGenerating 
                ? 'linear-gradient(135deg, #95a5a6, #7f8c8d)' 
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              padding: '18px 40px',
              fontSize: '17px',
              fontWeight: '700',
              borderRadius: '12px',
              cursor: isGenerating || selectedMonth === '' ? 'not-allowed' : 'pointer',
              boxShadow: isGenerating ? 'none' : '0 6px 20px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
              opacity: isGenerating || selectedMonth === '' ? 0.6 : 1,
              transform: 'translateY(0)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseEnter={(e) => {
              if (!isGenerating && selectedMonth !== '') {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isGenerating && selectedMonth !== '') {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
          >
            <span style={{fontSize: '24px'}}>
              {isGenerating ? '⏳' : '🎨'}
            </span>
            {isGenerating ? '⏳ Generazione in corso...' : (
              selectedUser !== 'all' 
                ? '📊 Genera Report Singolo Dipendente' 
                : selectedAzienda !== 'all'
                ? `📊 Genera Report ${searchAziendaTerm}`
                : selectedCantiere !== 'all'
                ? '📊 Genera Report Cantiere'
                : '📊 Genera Report Tutti i Dipendenti'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
export default AdminReports;